import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Music2,
  Calendar,
  Images,
  ShoppingCart,
  ShoppingBag,
  FolderTree,
  Package,
  FileText,
  FileEdit,
  UserCog,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Lock,
  Mail,
  Users,
} from "lucide-react";
import { useAdminAuth } from "../../services/admin/AdminAuthServices";
import { useComingSoon } from "../../utils/useComingSoon";


export default function AdminSidebar({ onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const location = useLocation(); // Get the current location
  const { adminLogout } = useAdminAuth();
  const { showComingSoon } = useComingSoon();
  const [activeItem, setActiveItem] = useState("");

  const navigationItems = useMemo(() => [
    { name: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
    { name: "Purchases & Orders", icon: ShoppingCart, path: "/admin/orders" },
    { name: "Contacts", icon: Mail, path: "/admin/contacts" },
    { name: "Discography", icon: Music2, path: "/admin/discography" },
    { name: "Events", icon: Calendar, path: "/admin/events" },
    { name: "Gallery", icon: Images, path: "/admin/gallery" },
    {
      name: "Shop & Music",
      icon: ShoppingCart,
      isParent: true,
      children: [
        {
          name: "Categories",
          icon: FolderTree,
          path: "/admin/inventory/categories",
        },
        {
          name: "Music Content",
          icon: Music2,
          path: "/admin/inventory/music"
        },
        {
          name: "Artist Merch",
          icon: ShoppingBag,
          path: "/admin/inventory/merch"
        },
        {
          name: "All Products",
          icon: Package,
          path: "/admin/inventory/products"
        },
      ],
    },
    { name: "Press", icon: FileText, path: "/admin/press" },
    { name: "EPK Management", icon: FileText, path: "/admin/epk" },
    {
      name: "Pages",
      icon: FileEdit,
      isParent: true,
      children: [
        { name: "Homepage", icon: FileText, path: "/admin/pages/homepage" },
        { name: "Biography", icon: FileText, path: "/admin/pages/biography" },
        { name: "Contact", icon: FileText, path: "/admin/pages/contact" },
      ],
    },
    { name: "Subscribers", icon: Users, path: "/admin/subscribers" },
    { name: "Admins", icon: UserCog, path: "/admin/admins" },
    { name: "Change Password", icon: Lock, path: "/admin/change-password" },
  ], []);

  // Helper function to check if a path is active
  const isPathActive = useCallback((itemPath) => {
    if (!itemPath) return false;
    if (itemPath === "/admin/dashboard") {
      return location.pathname === itemPath;
    }
    return location.pathname.startsWith(itemPath);
  }, [location.pathname]);

  // Update active item based on the current route
  useEffect(() => {
    const findActiveItem = (items) => {
      for (const item of items) {
        if (isPathActive(item.path)) {
          return item.name;
        }
        if (item.children) {
          for (const child of item.children) {
            if (isPathActive(child.path)) {
              return child.name;
            }
          }
        }
      }
      return null;
    };

    const activeName = findActiveItem(navigationItems);
    if (activeName) {
      setActiveItem(activeName);
    }
  }, [location.pathname, navigationItems, isPathActive]);

  const handleItemClick = (item) => {
    setActiveItem(item.name);

    // If the sidebar is closed on mobile, close it
    if (onClose && typeof window !== "undefined" && window.innerWidth < 1024) {
      onClose();
    }

    // If the item has no path, show coming soon modal
    if (!item.path) {
      showComingSoon({
        title: `${item.name} Coming Soon!`,
        message: `We're building the ${item.name} feature. It will be available in the next update. Stay tuned!`,
      });
      return;
    }
  };

  const handleLogout = async () => {
    try {
      ;
      await adminLogout(navigate); // ✅ backend logout + cleanup + redirect handled inside
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("admin");
      localStorage.removeItem("admin_token");
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <div
      className={`${collapsed ? "w-20" : "w-64"
        } bg-white dark:bg-[#1A1A1A] flex-shrink-0 flex flex-col h-full border-r border-[#E5E7EB] dark:border-[#2A2A2A] transition-all duration-300`}
    >
      {/* Brand Logo */}
      <div className="p-6 flex items-center justify-between border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-jazz-brown/30 rounded-xl flex items-center justify-center shadow-lg">
              <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun Logo" loading="eager" />
            </div>
            <div>
              <h2 className="font-bold text-[#111111] dark:text-white text-sm font-poppins">
                Temidayo Balogun
              </h2>
              <p className="text-xs text-black dark:text-[#A0A0A0] font-inter">
                Admin Dashboard
              </p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-10 h-10 border border-jazz-brown/30 rounded-xl flex items-center justify-center shadow-lg mx-auto">
            <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun Logo" loading="eager" />
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.name;

            if (item.isParent) {
              const isAnyChildActive = item.children?.some(child => isPathActive(child.path));
              return (
                <div key={item.name} className="space-y-1">
                  {/* Parent Item */}
                  <div
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${collapsed ? "justify-center" : ""
                      } ${isAnyChildActive
                        ? "text-jazz-brown bg-jazz-brown/5 dark:bg-jazz-brown/10 border-2 border-jazz-brown/80 shadow-sm"
                        : "text-black dark:text-[#A0A0A0] border-2 border-transparent"}`}
                    title={collapsed ? item.name : ""}
                  >
                    <Icon
                      size={20}
                      strokeWidth={isAnyChildActive ? 2.5 : 2}
                      className={isAnyChildActive ? "text-jazz-brown" : ""}
                    />
                    {!collapsed && (
                      <span className="font-medium text-sm font-inter">
                        {item.name}
                      </span>
                    )}
                  </div>

                  {/* Always visible children */}
                  {!collapsed && item.children && (
                    <div className="ml-3 pl-4 border-l-2 border-[#E5E7EB] dark:border-[#2A2A2A] space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <NavLink
                            key={child.name}
                            to={child.path}
                            onClick={() => handleItemClick(child)}
                            className={({ isActive: isChildActive }) => `w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isChildActive
                              ? "bg-jazz-brown/10 dark:bg-jazz-brown/20 border-2 border-jazz-brown/80 text-jazz-brown dark:text-jazz-brown shadow-sm font-semibold"
                              : "text-black dark:text-[#A0A0A0] border-2 border-transparent hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white"
                              }`}
                          >
                            <ChildIcon
                              size={18}
                              className={
                                activeItem === child.name || location.pathname.startsWith(child.path)
                                  ? "text-jazz-brown"
                                  : ""
                              }
                              strokeWidth={activeItem === child.name || location.pathname.startsWith(child.path) ? 2.5 : 2}
                            />
                            <span
                              className={`text-sm font-inter ${activeItem === child.name || location.pathname.startsWith(child.path) ? "font-semibold" : "font-medium"}`}
                            >
                              {child.name}
                            </span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular menu items
            return (
              <NavLink
                key={item.name}
                to={item.path}
                onClick={() => handleItemClick(item)}
                className={({ isActive }) => `w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${isActive
                  ? "bg-jazz-brown/10 dark:bg-jazz-brown/20 border-2 border-jazz-brown/80 text-jazz-brown dark:text-jazz-brown shadow-sm font-semibold"
                  : "text-black dark:text-[#A0A0A0] border-2 border-transparent hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white active:bg-[#F3F4F6] dark:active:bg-[#2A2A2A] active:scale-[0.98]"
                  }`}
                title={collapsed ? item.name : ""}
              >
                <Icon
                  size={20}
                  className={
                    isActive ? "text-jazz-brown" : ""
                  }
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {!collapsed && (
                  <span
                    className={`font-medium text-sm font-inter ${isActive ? "font-semibold" : ""}`}
                  >
                    {item.name}
                  </span>
                )}
              </NavLink>
            );
          })}


          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 active:scale-[0.98] mt-2"
            title={collapsed ? "Logout" : ""}
          >
            <LogOut size={20} strokeWidth={2} />
            {!collapsed && (
              <span className="font-medium text-sm font-inter">Logout</span>
            )}
          </button>
        </div>
      </nav>

      {/* Collapse Toggle - Desktop Only */}
      <div className="hidden lg:block p-3 border-t border-[#E5E7EB] dark:border-[#2A2A2A]">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-black dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#111111] dark:hover:text-white transition-all duration-200 active:scale-95"
        >
          {collapsed ? (
            <ChevronRight size={18} />
          ) : (
            <>
              <ChevronLeft size={18} />
              <span className="text-sm font-medium font-inter">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
